"use client";

import { useState, Suspense, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Link from "@/components/LocalizedLink";
import { useSearchParams } from "next/navigation";
import { useTranslation } from "@/lib/i18n";
import { trackEvent } from "@/lib/analytics";
import { HeroMiniPricer } from "@/components/HeroMiniPricer";
import {
  getCTAVariant,
  CTA_VARIANT_LABELS,
  CTA_VARIANT_LABELS_BG,
  trackVariantExposure,
  type CTAVariant,
} from "@/lib/ab-test";

/* ── Personalized Sub-Headline ─────────────────────────────────────────── */
//
// Kept from the pre-redesign hero. HeroHeader ships the default subhead in
// SSR (data-hero-default-subtitle); once this component hydrates, it swaps
// in a UTM- or returning-visitor-specific line if applicable and hides the
// SSR default via a scoped inline <style>. If nothing personalized applies,
// the SSR line stays exactly where it was and CLS stays at 0.

function PersonalizedSubtitle({
  defaultSubtitle,
}: {
  defaultSubtitle: string;
}) {
  const searchParams = useSearchParams();
  const [subtitle, setSubtitle] = useState<string | null>(null);

  useEffect(() => {
    const utmSource = searchParams.get("utm_source");
    const utmCampaign = searchParams.get("utm_campaign");

    let utmText: string | null = null;

    if (utmSource === "peeringdb") {
      utmText =
        "Direct peering at MegaIX Sofia, DE-CIX Frankfurt, GR-IX Athens";
    } else if (utmSource === "linkedin") {
      utmText =
        "The carrier corridor between Middle East, Caucasus and Europe \u2014 on one AS.";
    } else if (utmCampaign === "ip-transit") {
      utmText = "IP Transit from \u20ac120/month \u2014 1G to 400G, Sofia or Frankfurt";
    }

    if (utmText) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSubtitle(utmText);
      return;
    }

    // NOTE: a returning-visitor personalization used to live here, keyed on
    // localStorage["sc-visited"]. It was removed in fix/compliance-gaps —
    // a returning-visitor flag is not "strictly necessary" under the
    // ePrivacy Directive (it does not deliver a service the user actively
    // requested), and gating it behind the Analytics consent category
    // would surface a personalization toggle we don't otherwise offer.
    // The default subtitle is used for everyone; the UTM branch above
    // still runs for campaign-specific copy.
  }, [searchParams, defaultSubtitle]);

  if (subtitle === null) return null;

  return (
    <>
      <style>{`[data-hero-default-subtitle]{display:none}`}</style>
      <div className="mt-4 min-h-[3rem] flex items-center justify-center">
        <AnimatePresence mode="wait">
          <motion.p
            key={subtitle}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className="text-[15px] leading-relaxed text-muted-foreground max-w-2xl mx-auto"
          >
            {subtitle}
          </motion.p>
        </AnimatePresence>
      </div>
    </>
  );
}

/* ── Hero Section ──────────────────────────────────────────────────────── */
//
// Post-redesign the hero is a typographic statement. `header` (a server
// component, typically <HeroHeader />) ships eyebrow + h1 + subhead +
// hairline + latency proof points as static HTML so the LCP element paints
// before JS. This client wrapper only owns:
//   - PersonalizedSubtitle (UTM/returning-visitor swap of the subhead)
//   - CTA pair — primary "Try NaaS Configurator" with the A/B copy hook,
//     secondary "Explore Network" (border, text-foreground, no glow)
//   - HeroMiniPricer, staggered below (Task 3 defaults it to 1G)

export function HeroSection({ header }: { header?: React.ReactNode }) {
  const [ctaVariant] = useState<CTAVariant>(() => getCTAVariant());
  const { t, language } = useTranslation();

  useEffect(() => {
    trackVariantExposure(ctaVariant);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section className="relative min-h-screen flex flex-col items-center justify-center overflow-x-clip overflow-y-visible bg-background pt-24 pb-16">
      {/*
        Signal backdrop — grid-lines quadrant + a very soft radial teal wash.
        Grid-lines are a 48px CSS gradient pattern (no SVG, no images), fading
        outwards via a mask so the edges do not slam into the section's fade.
        The teal radial replaces the previous blue radial — same purpose (keep
        the hero from reading as a blank canvas), tuned to the accent hue so
        it does not compete with the CTA glow.
      */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div
          className="absolute inset-0 signal-grid-lines opacity-[0.4]"
          style={{
            WebkitMaskImage:
              "radial-gradient(ellipse at 50% 40%, black 0%, transparent 70%)",
            maskImage:
              "radial-gradient(ellipse at 50% 40%, black 0%, transparent 70%)",
          }}
        />
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] bg-[radial-gradient(ellipse,rgba(0,212,170,0.05)_0%,transparent_70%)]" />
      </div>

      {/* SSR header (eyebrow, h1, subhead, hairline, latency proofs). */}
      {header}

      <div className="relative z-10 w-full max-w-3xl mx-auto px-4 text-center">
        {/* Personalized subhead swap. fallback={null} keeps CLS at 0 while
            React hydrates — the SSR default from HeroHeader is already on
            screen, so a placeholder here would only cause a text flicker. */}
        <Suspense fallback={null}>
          <PersonalizedSubtitle defaultSubtitle={t("hero_subtitle")} />
        </Suspense>

        {/* CTAs — exactly one primary, one secondary. Both entrance-animated
            together after the SSR block paints. */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.4, ease: "easeOut" }}
          className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3"
        >
          {/* Primary — bg-accent + text-on-accent + glow-teal. Tailwind
              tokens all the way through so both themes work without an
              inline style; the tokens change value between dark and light. */}
          <Link
            href="/#configurator"
            onClick={() =>
              trackEvent("cta_try_naas_configurator", {
                location: "hero",
                ab_variant: ctaVariant,
              })
            }
            className="inline-flex items-center gap-2 px-6 py-3 rounded-[2px] text-sm font-semibold bg-accent text-on-accent transition-[filter,box-shadow] duration-150 hover:brightness-95"
            style={{ boxShadow: "0 0 24px var(--glow-teal)" }}
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M13 10V3L4 14h7v7l9-11h-7z"
              />
            </svg>
            <span>{(language === "bg" ? CTA_VARIANT_LABELS_BG : CTA_VARIANT_LABELS)[ctaVariant]}</span>
          </Link>

          {/* Secondary — border + text-foreground + hover:bg-surface-light. */}
          <Link
            href="/#network-mesh"
            onClick={() =>
              trackEvent("cta_explore_network", { location: "hero" })
            }
            className="inline-flex items-center gap-2 px-6 py-3 rounded-[2px] text-sm font-medium border border-border text-foreground hover:bg-surface-light transition-colors duration-150"
          >
            {t("explore_network")}
          </Link>
        </motion.div>

        {/* Mini-pricer — staggered below CTAs. Task 3 (see HeroMiniPricer.tsx)
            defaults it to 1G so its "€120/mo" matches the main configurator's
            starting price and the two surfaces no longer contradict. */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25, duration: 0.4, ease: "easeOut" }}
        >
          <HeroMiniPricer />
        </motion.div>
      </div>

      {/* Bottom fade into next section. */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent" />
    </section>
  );
}
